import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  UserCircle,
  MagnifyingGlass,
  Shield,
  CheckCircle,
  XCircle,
  Plus,
  Trash,
  Key,
  SlidersHorizontal
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ROLE_LABELS = {
  admin: "Administrateur",
  commercial: "Commercial",
  preparateur: "Préparateur",
  auditeur: "Auditeur",
  comptabilite: "Comptabilité"
};

const PERMISSION_MODULES = [
  { key: "vehicles", label: "Véhicules", actions: ["read", "create", "update", "delete"] },
  { key: "clients", label: "Clients", actions: ["read", "create", "update", "delete"] },
  { key: "invoices", label: "Factures", actions: ["read", "create", "update"] },
  { key: "preparation", label: "Préparation", actions: ["read", "update"] },
  { key: "audits", label: "Audits", actions: ["read", "create"] },
  { key: "settings", label: "Paramètres", actions: ["read", "update"] },
  { key: "users", label: "Utilisateurs", actions: ["read", "create", "update", "delete"] },
];

const ACTION_LABELS = {
  read: "Voir",
  create: "Créer",
  update: "Modifier",
  delete: "Supprimer"
};

const authHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {  };
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "commercial" });
  const [newPasswordDialog, setNewPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordUserId, setPasswordUserId] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, authHeaders());
      setUsers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, newUser, authHeaders());
      toast.success(`Utilisateur ${newUser.name} créé`);
      setCreateDialogOpen(false);
      setNewUser({ email: "", name: "", password: "", role: "commercial" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, authHeaders());
      toast.success("Utilisateur supprimé");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, null, { ...authHeaders(), params: { role: newRole } });
      toast.success("Rôle mis à jour");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleStatusChange = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}/status`, null, { ...authHeaders(), params: { is_active: isActive } });
      toast.success(isActive ? "Utilisateur activé" : "Utilisateur désactivé");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const openPermissions = (user) => {
    setSelectedUser(user);
    setEditPerms(user.permissions || {});
    setPermDialogOpen(true);
  };

  const togglePerm = (module, action) => {
    const current = editPerms[module] || [];
    const updated = current.includes(action) ? current.filter(a => a !== action) : [...current, action];
    setEditPerms({ ...editPerms, [module]: updated });
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API}/users/${selectedUser.user_id}/permissions`, { permissions: editPerms }, authHeaders());
      toast.success("Permissions mises à jour");
      setPermDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    try {
      await axios.put(`${API}/users/${passwordUserId}/password`, { new_password: newPassword }, authHeaders());
      toast.success("Mot de passe réinitialisé");
      setNewPasswordDialog(false);
      setNewPassword("");
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  const filteredUsers = users.filter(u => {
    const search = searchTerm.toLowerCase();
    return u.name?.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search);
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-10 w-10"></div></div>;
  }

  return (
    <div data-testid="users-page" className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Utilisateurs</h1>
          <p className="page-subtitle">{filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="page-actions">
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-user-btn">
                <Plus size={18} className="mr-2" /> Ajouter un utilisateur
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-heading-3">Nouvel utilisateur</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div><Label className="form-label">Nom complet *</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required data-testid="new-user-name" /></div>
                <div><Label className="form-label">Email *</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required data-testid="new-user-email" /></div>
                <div><Label className="form-label">Mot de passe temporaire *</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required minLength={6} data-testid="new-user-password" /></div>
                <div><Label className="form-label">Rôle</Label>
                  <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" className="btn-primary" data-testid="submit-new-user">Créer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-users" placeholder="Rechercher par nom ou email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="empty-state"><UserCircle size={56} className="empty-state-icon" /><p className="empty-state-title">Aucun utilisateur trouvé</p></div>
      ) : (
        <div className="card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Statut</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isSelf = u.user_id === currentUser?.user_id;
                return (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">{u.name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-slate-900">{u.name}</span>
                      </div>
                    </td>
                    <td className="text-slate-600 text-xs">{u.email}</td>
                    <td>
                      <Select value={u.role} onValueChange={(val) => handleRoleChange(u.user_id, val)} disabled={isSelf}>
                        <SelectTrigger className="w-40" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                      </Select>
                    </td>
                    <td>
                      {u.is_active !== false ? (
                        <span className="flex items-center gap-1 text-emerald-700 text-xs font-medium"><CheckCircle size={14} weight="fill" /> Actif</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600 text-xs font-medium"><XCircle size={14} weight="fill" /> Inactif</span>
                      )}
                    </td>
                    <td><Switch checked={u.is_active !== false} onCheckedChange={(checked) => handleStatusChange(u.user_id, checked)} disabled={isSelf} data-testid={`status-switch-${u.user_id}`} /></td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => openPermissions(u)} data-testid={`perm-btn-${u.user_id}`} title="Permissions">
                          <SlidersHorizontal size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setPasswordUserId(u.user_id); setNewPasswordDialog(true); }} title="Réinitialiser mot de passe">
                          <Key size={14} />
                        </Button>
                        {!isSelf && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" data-testid={`delete-user-${u.user_id}`} title="Supprimer">
                                <Trash size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Supprimer {u.name} ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteUser(u.user_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-heading-3">Permissions de {selectedUser?.name}</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-4">
            {PERMISSION_MODULES.map((mod) => (
              <div key={mod.key} className="p-3 rounded-lg border border-slate-200">
                <p className="font-semibold text-slate-900 text-sm mb-2">{mod.label}</p>
                <div className="flex flex-wrap gap-3">
                  {mod.actions.map((action) => (
                    <label key={action} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={(editPerms[mod.key] || []).includes(action)} onCheckedChange={() => togglePerm(mod.key, action)} data-testid={`perm-${mod.key}-${action}`} />
                      <span className="text-sm text-slate-700">{ACTION_LABELS[action]}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setPermDialogOpen(false)}>Annuler</Button>
            <Button className="btn-primary" onClick={handleSavePermissions} data-testid="save-permissions-btn">Enregistrer les permissions</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={newPasswordDialog} onOpenChange={setNewPasswordDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Réinitialiser le mot de passe</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-4">
            <div><Label className="form-label">Nouveau mot de passe *</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6} data-testid="reset-password-input" /></div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setNewPasswordDialog(false)}>Annuler</Button>
              <Button className="btn-primary" onClick={handleResetPassword} data-testid="confirm-reset-password">Réinitialiser</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
